import { AxiosError } from "axios";
import { AddContainmentResponse, AddSerialsResponse, ContainmentErrorResponse } from "lib/api";
import * as result from "lib/result";
import { buildSubmitContainmentPayload } from "lib/util/buildSubmitContainmentPayload";
import { useCallback, useState } from "react";

/**
 * Hook that orchestrates containment submission for create, add serials, lock, and
 * unlock flows.
 *
 * Manages the submission lifecycle including:
 * - Loading state during API calls
 * - Error handling and reporting
 * - Response data storage
 * - Step status updates for multi-step workflows
 *
 * Supports four modes:
 * - Create new containment (default)
 * - Add serials to existing containment (addSerialMode, Flexnet)
 * - Lock LPNs/serials (addSerialMode + lockMode, ASRS)
 * - Unlock LPNs/serials (addSerialMode + unlockMode, ASRS)
 */
// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
export function useContainmentSubmit({
	mccApi,
	formValues,
	id,
	addSerialMode,
	lockMode,
	unlockMode,
	byLpn,
	setStepStatus,
}: {
	mccApi: any;
	formValues: any;
	id?: string;
	addSerialMode: boolean;
	lockMode?: boolean;
	unlockMode?: boolean;
	byLpn?: boolean;
	setStepStatus: (status: any) => void;
}) {
	const [status, setStatus] = useState<"NotAsked" | "Loading" | "Done">("NotAsked");
	const [error, setError] = useState<AxiosError<ContainmentErrorResponse> | null>(null);
	const [data, setData] = useState<AddContainmentResponse | AddSerialsResponse | null>(
		null
	);

	const submit = useCallback(async () => {
		setStatus("Loading");
		try {
			if (addSerialMode) {
				if (unlockMode) {
					const response = await mccApi.UnlockContainment(
						formValues.file.Expect("no file selected"),
						id,
						byLpn ?? true,
						{ headers: { "Content-Type": "multipart/form-data" } }
					);
					setData(response as any);
					setStepStatus(result.Ok({ formProgress: "completed" }));
					setStatus("Done");
					return;
				}

				const response = await mccApi.AddSerials(
					formValues.file.Expect("no file selected"),
					id,
					lockMode ? byLpn : undefined,
					{ headers: { "Content-Type": "multipart/form-data" } }
				);
				setData(response);
				setStepStatus(result.Ok({ formProgress: "completed" }));
				setStatus("Done");
				return;
			}

			const reasonList = await mccApi.ContainmentReasons();
			const reason = reasonList.First();
			if (reason.IsNone()) {
				throw new Error("No reason found");
			}

			const payload = buildSubmitContainmentPayload({
				...formValues,
				reason: reason.Expect("no reason"),
			});

			const response = await mccApi.AddContainment(payload, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			setData(response);
			setStepStatus(result.Ok({ formProgress: "completed" }));
			setStatus("Done");
		} catch (err) {
			setError(err as AxiosError<ContainmentErrorResponse>);
			setStepStatus(result.Err(err as Error));
			setStatus("Done");
		}
	}, [
		addSerialMode,
		byLpn,
		formValues,
		id,
		lockMode,
		mccApi,
		setStepStatus,
		unlockMode,
	]);

	return {
		status,
		error,
		data,
		submit,
	};
}
